import { useTranslation } from "react-i18next"
import { AutomationCentreWrapper } from "./styles"
import { FormProvider, useForm } from "react-hook-form"
import ActionButtons from "./components/actionButtons"
import BaseSection from "./components/baseSection"
import ProcedureSection from "./components/procedureSection"
import TimingSection from "./components/timingSection"
import SettingSection from "./components/settingSection"
import {
	useGetAutomationById,
	useGetMasterData,
	usePostUpdateAutomation,
} from "@services"
import { useContext, useEffect } from "react"
import { IGetAutomationByIdSenderChannel } from "src/services/Automations/GetAutomationById/types"
import { GetAllAutomationsUrl } from "../../utils/constants"
import { useQueryClient } from "@tanstack/react-query"
import { ToastMessageContext } from "@context/toast-message-cotnext"
import { GoArrowLeft, GoArrowRight } from "react-icons/go"

interface IAutomationCentreProps {
	onclose: () => void
	id?: number
	templetName: string | undefined
}
export interface IFormAutomationCentre {
	description: string
	message: string
	eventType: string
	id: number
	senderChannels: IGetAutomationByIdSenderChannel[]
	eventTiming: string
	time: string
	timeType: string
	triggerType: number[]
	properties: number[]
	Name: string
}
export default function AutomationCentre({
	onclose,
	id,
	templetName,
}: IAutomationCentreProps) {
	/* ---------------------------------- API'S --------------------------------- */
	const { data } = useGetMasterData({
		key: "automations",
	})

	const { data: GetAutomationById } = useGetAutomationById(
		{
			id: id!,
		},
		{ enabled: id !== undefined }
	)
	const { mutateAsync: mutateAsyncPostUpdateAutomation } =
		usePostUpdateAutomation()

	/* ---------------------------------- Hooks --------------------------------- */
	const {
		t,
		i18n: { language },
	} = useTranslation()
	const queryClient = useQueryClient()
	const { showSuccess, showWarning } = useContext(ToastMessageContext)

	const methods = useForm<IFormAutomationCentre>()
	const { setValue } = methods

	const onSubmit = async (data: IFormAutomationCentre) => {
		if (data.properties.length === 0)
			return showWarning(
				t(
					"Automation.you_must_select_at_least_one_property_to_enable_automation"
				)
			)
		if (!data.senderChannels?.map((item) => item?.isActive)?.includes(true))
			return showWarning(
				t(
					"Automation.please_select_at_least_one_communication_channel_to_enable_automation"
				)
			)
		const formattedData = {
			...data,
			PropertiesIds: data.properties,
		}

		const response = await mutateAsyncPostUpdateAutomation(formattedData)
		if (response.status === 200) {
			onclose()
			queryClient.invalidateQueries({ queryKey: [GetAllAutomationsUrl] })
			showSuccess(t("Automation.updated_successfully"))
		}
	}

	/* ------------------------------- Life cycle ------------------------------- */
	useEffect(() => {
		setValue("Name", templetName ?? "")
		setValue("triggerType", GetAutomationById?.result?.triggerType ?? [])
		setValue("id", GetAutomationById?.result?.id!)
		setValue("description", GetAutomationById?.result?.description ?? "")
		setValue("message", GetAutomationById?.result?.message ?? "")
		setValue("eventType", GetAutomationById?.result?.eventType.toString() ?? "")
		setValue("senderChannels", GetAutomationById?.result?.senderChannels ?? [])
		setValue(
			"eventTiming",
			GetAutomationById?.result?.eventTiming?.toString() ?? ""
		)
		setValue("time", GetAutomationById?.result?.time?.toString() ?? "")
		setValue("timeType", GetAutomationById?.result?.timeType?.toString() ?? "")
		setValue("properties", [])
	}, [GetAutomationById])
	return (
		<AutomationCentreWrapper $lang={language as "ar" | "en"}>
			<div className='backAction'>
				<div onClick={onclose}>
					{language === "ar" ? <GoArrowLeft /> : <GoArrowRight />}
				</div>
			</div>
			<FormProvider {...methods}>
				<form onSubmit={methods.handleSubmit(onSubmit)}>
					<div className='containerFormElements'>
						<ActionButtons />
						<BaseSection listData={data?.result?.eventTypes ?? []} />
						<ProcedureSection
							automationVariable={data?.result?.automationVariable ?? []}
							triggerType={data?.result.triggerTypes ?? []}
						/>
						<TimingSection
							eventTimings={data?.result.eventTimings ?? []}
							listData={data?.result?.eventTypes ?? []}
						/>
						<SettingSection
							listSelectedPropertiesData={
								GetAutomationById?.result?.properties.map((item) => item.id) ??
								[]
							}
						/>
					</div>
				</form>
			</FormProvider>
		</AutomationCentreWrapper>
	)
}
